import React, { useMemo, useRef, useState } from "react";
import html2canvas from "html2canvas";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Card from "@mui/joy/Card";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Input from "@mui/joy/Input";
import Option from "@mui/joy/Option";
import Select from "@mui/joy/Select";
import Stack from "@mui/joy/Stack";
import Textarea from "@mui/joy/Textarea";
import Typography from "@mui/joy/Typography";

const badgeTypes = {
    diamond: {
        label: "Diamante",
        title: "Informação Diamante",
        img: "/black-diamond-1.png",
        className: "diamond",
    },
    gold: {
        label: "Ouro",
        title: "Informação Ouro",
        img: "/gold-bar-1.png",
        className: "gold",
    },
    star: {
        label: "Importante",
        title: "Informação Importante",
        img: "/blue-star-1.png",
        className: "star",
    },
    danger: {
        label: "Perigo / Atenção",
        title: "Atenção",
        img: "/red-danger-1.png",
        className: "danger",
    },
    target: {
        label: "Objetivo",
        title: "Objetivo",
        img: "/red-target-1.png",
        className: "target",
    },
};

function renderBoldMarkdown(text) {
    return text.split(/(\*\*[^*]+\*\*)/g).map((part, index) => {
        if (part.startsWith("**") && part.endsWith("**")) {
            return <strong key={index}>{part.slice(2, -2)}</strong>;
        }
        return <React.Fragment key={index}>{part}</React.Fragment>;
    });
}

export default function BadgeBuilder() {
    const [type, setType] = useState("diamond");
    const [title, setTitle] = useState(badgeTypes.diamond.title);
    const [text, setText] = useState(
        "São aquelas que você levará para **toda a sua vida nos negócios**."
    );
    const [width, setWidth] = useState(700);
    const exportRef = useRef(null);

    const selected = badgeTypes[type];

    function handleTypeChange(_, nextType) {
        if (!nextType) return;
        setType(nextType);
        setTitle(badgeTypes[nextType].title);
    }

    const htmlCode = useMemo(() => {
        const safeTitle = title || selected.title;
        const htmlText = text.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");

        return `<div class="badge ${selected.className}">
  <img src="https://ananocastor.com.br/files/static${selected.img}" />
  <div class="badge-text">
    <div><strong>${safeTitle}</strong></div>
    <div>${htmlText}</div>
  </div>
</div>`;
    }, [selected, title, text]);

    async function downloadPng() {
        if (!exportRef.current) return;

        const sourceCanvas = await html2canvas(exportRef.current, {
            backgroundColor: null,
            scale: 2,
            useCORS: true,
            allowTaint: false,
        });

        const finalCanvas = document.createElement("canvas");
        finalCanvas.width = sourceCanvas.width;
        finalCanvas.height = sourceCanvas.height;

        const ctx = finalCanvas.getContext("2d");

        ctx.shadowColor = "rgba(0, 0, 0, 0.58)";
        ctx.shadowBlur = 44;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 12;

        ctx.drawImage(sourceCanvas, 0, 0);

        ctx.shadowColor = "transparent";
        ctx.drawImage(sourceCanvas, 0, 0);

        const link = document.createElement("a");
        link.download = `badge-${type}.png`;
        link.href = finalCanvas.toDataURL("image/png");
        link.click();
    }

    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "neutral.100", p: 3 }}>
            <style>{`
        .badge-export-area {
          width: 1080px;
          background: transparent;
          display: flex;
          justify-content: center;
          align-items: flex-start;
          box-sizing: border-box;
          padding: 30px;

        }

        .badge {
          border: 1px solid rgb(84, 85, 84);
          background: #f3fff3;
          color: #434643;
          padding: 12px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 10px;
          box-sizing: border-box;
          font-family: Arial, Helvetica, sans-serif;
          font-size: 16px;
          line-height: 1.35;
        }
          .badge-shadow {
                filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.52));
            }


        .badge img {
          width: 60px;
          height: auto;
          flex: 0 0 auto;
        }

        .badge .badge-text {
          flex: 1;
          min-width: 0;
          text-align: left;
        }

        .badge .badge-text > div:first-child {
          margin-bottom: 4px;
        }

        .badge .badge-text strong {
          white-space: nowrap;
        }

        .badge strong {
          white-space: nowrap;
          display: inline-block;
        }

        .badge.diamond {
          border-color: #000000;
          background: #cacaca;
          color: #313131;
        }

        .badge.diamond strong {
          color: #000000;
        }

        .badge.gold {
          border-color: #b8860b;
          background: #fff3b0;
          color: #3a2a00;
        }

        .badge.gold strong {
          color: #b8860b;
        }

        .badge.star {
          border-color: #0b4f8a;
          background: #d8ecff;
          color: #062a4a;
        }

        .badge.star strong {
          color: #0b4f8a;
        }

        .badge.danger {
          border-color: #7a0000;
          background: #ffd6d6;
          color: #3b0000;
        }

        .badge.danger strong {
          color: #7a0000;
        }

        .badge.target {
          border-color: #b90101;
          background: #f8f8f8;
          color: #3b0000;
        }

        .badge.target strong {
          color: #b90101;
        }
      `}</style>

            <Box
                sx={{
                    maxWidth: 1320,
                    mx: "auto",
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", lg: "360px 1fr" },
                    gap: 3,
                }}
            >
                <Card variant="outlined" sx={{ p: 3 }}>
                    <Typography level="h3" sx={{ mb: 2 }}>
                        Badge Builder
                    </Typography>

                    <Stack spacing={2}>
                        <FormControl>
                            <FormLabel>Tipo</FormLabel>
                            <Select value={type} onChange={handleTypeChange}>
                                {Object.entries(badgeTypes).map(([key, value]) => (
                                    <Option key={key} value={key}>
                                        {value.label}
                                    </Option>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl>
                            <FormLabel>Título</FormLabel>
                            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Texto</FormLabel>
                            <Textarea
                                minRows={7}
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                placeholder="Use **texto** para negrito"
                            />
                            <Typography level="body-xs" sx={{ mt: 0.5, color: "neutral.500" }}>
                                Use **texto** para deixar partes em negrito.
                            </Typography>
                        </FormControl>

                        <FormControl>
                            <FormLabel>Largura da badge</FormLabel>
                            <Input
                                type="number"
                                value={width}
                                onChange={(e) => setWidth(Number(e.target.value) || 600)}
                            />
                        </FormControl>

                        <Button size="lg" color="neutral" onClick={downloadPng}>
                            Download PNG 1080px
                        </Button>
                    </Stack>
                </Card>

                <Card variant="outlined" sx={{ p: 3 }}>
                    <Typography level="h3" sx={{ mb: 2 }}>
                        Preview
                    </Typography>

                    <Box
                        sx={{
                            overflow: "auto",
                            border: "1px dashed",
                            borderColor: "neutral.300",
                            borderRadius: "md",
                            p: 2,
                            backgroundColor: "#fff",
                        }}
                    >
                        <Box ref={exportRef} className="badge-export-area">
                            <Box className="badge-shadow">
                                <Box className={`badge ${selected.className}`} sx={{ width }}>
                                    <img src={"https://ananocastor.com.br/files/static" + selected.img} crossOrigin="anonymous" alt="" />
                                    <Box className="badge-text">
                                        <Box>
                                            <strong>{title || selected.title}</strong>
                                        </Box>
                                        <Box>{renderBoldMarkdown(text)}</Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ mt: 3 }}>
                        <Typography level="title-sm" sx={{ mb: 1 }}>
                            HTML gerado
                        </Typography>
                        <Box
                            component="pre"
                            sx={{
                                maxHeight: 280,
                                overflow: "auto",
                                bgcolor: "neutral.900",
                                color: "common.white",
                                borderRadius: "md",
                                p: 2,
                                fontSize: 12,
                                whiteSpace: "pre-wrap",
                            }}
                        >
                            {htmlCode}
                        </Box>
                    </Box>
                </Card>
            </Box>
        </Box>
    );
}
